'use client';

import useSWR from 'swr';
import { useCallback } from 'react';

import { endpoints } from 'src/lib/axios';
import { useAppContext } from 'src/context/app';

import { toast } from 'src/components/snackbar';

import type { KomIgångStatus } from './kom-igang-typer';
import { TOMT_FRAMSTEG, sättAvbockning, hämtaKomIgångStatus } from './KomIgangService';

// ----------------------------------------------------------------------

/**
 * Organisationens kom igång-framsteg.
 *
 * `revalidateOnFocus` är poängen med sidan: man klickar sig iväg, gör steget i den riktiga vyn
 * och kommer tillbaka — då ska bocken sitta där utan att man laddar om.
 */
export function useKomIgång() {
    // Framsteget är föreningens och hämtas via `X-Organisation-Id`. Utan förening
    // ("Alla organisationer") skickas inget huvud, och anropet kan bara sluta med 400 —
    // så det görs inte alls.
    const { organisation } = useAppContext();

    const { data, isLoading, error, mutate } = useSWR<KomIgångStatus>(
        organisation ? endpoints.komIgång.status : null,
        hämtaKomIgångStatus,
        { revalidateOnFocus: true, keepPreviousData: true }
    );

    const växlaSteg = useCallback(
        async (stegId: string, avbockad: boolean) => {
            const nuvarande = data ?? TOMT_FRAMSTEG;

            // Optimistiskt: en kryssruta ska kännas omedelbar. Namnet på den som bockade av
            // fylls i av servern, så raden får sin förklaring först efter svaret.
            const optimistisk: KomIgångStatus = {
                ...nuvarande,
                avbockade: avbockad
                    ? [
                          ...nuvarande.avbockade.filter((a) => a.stegId !== stegId),
                          { stegId, avbockadAv: null, avbockadAvNamn: null, avbockadVid: new Date().toISOString() },
                      ]
                    : nuvarande.avbockade.filter((a) => a.stegId !== stegId),
            };

            try {
                await mutate(sättAvbockning(stegId, avbockad), {
                    optimisticData: optimistisk,
                    rollbackOnError: true,
                    revalidate: false,
                });
            } catch (fel) {
                console.error('Kunde inte spara avbockningen', fel);
                toast.error('Kunde inte spara. Försök igen.');
            }
        },
        [data, mutate]
    );

    return { status: data, laddar: isLoading, fel: error, växlaSteg, uppdatera: mutate };
}
